import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import { useToast } from '../context/ToastContext';
import { usePlan, LIMITES } from '../context/PlanContext';
import Quagga from '@ericblade/quagga2';
import { BookOpen } from 'lucide-react';

const GOOGLE_BOOKS_KEY = 'AIzaSyAO-Ixesp3N_WRHQZZ4QHLbQBVoPBdKKJ0';

const GENEROS_LIST = [
  { id: 'ficcao', label: 'Ficção' }, { id: 'romance', label: 'Romance' },
  { id: 'fantasia', label: 'Fantasia' }, { id: 'sci-fi', label: 'Ficção Científica' },
  { id: 'terror', label: 'Terror' }, { id: 'misterio', label: 'Mistério' },
  { id: 'thriller', label: 'Thriller' }, { id: 'autoajuda', label: 'Autoajuda' },
  { id: 'negocios', label: 'Negócios' }, { id: 'biografia', label: 'Biografia' },
  { id: 'historia', label: 'História' }, { id: 'ciencia', label: 'Ciência' },
  { id: 'filosofia', label: 'Filosofia' }, { id: 'poesia', label: 'Poesia' },
  { id: 'hq', label: 'HQ / Quadrinhos' }, { id: 'manga', label: 'Mangá' },
  { id: 'jovem-adulto', label: 'Jovem Adulto' }, { id: 'infantil', label: 'Infantil' },
  { id: 'religioso', label: 'Religioso/Espiritual' }, { id: 'saude', label: 'Saúde & Bem-estar' },
  { id: 'tecnologia', label: 'Tecnologia' }, { id: 'culinaria', label: 'Culinária' },
  { id: 'esportes', label: 'Esportes' }, { id: 'cronicas', label: 'Crônicas' },
  { id: 'outro', label: 'Outro' },
];

const CATEGORIA_MAP = {
  'fiction': 'ficcao', 'literary fiction': 'ficcao', 'romance': 'romance',
  'love stories': 'romance', 'fantasy': 'fantasia', 'fantasy fiction': 'fantasia',
  'science fiction': 'sci-fi', 'horror': 'terror', 'mystery': 'misterio',
  'detective and mystery stories': 'misterio', 'self-help': 'autoajuda',
  'business': 'negocios', 'business & economics': 'negocios',
  'biography': 'biografia', 'biography & autobiography': 'biografia',
  'history': 'historia', 'science': 'ciencia', 'philosophy': 'filosofia',
  'poetry': 'poesia', 'comics & graphic novels': 'hq', 'juvenile fiction': 'infantil',
  'juvenile': 'infantil', 'young adult fiction': 'jovem-adulto', 'young adult': 'jovem-adulto',
  'religion': 'religioso', 'thrillers': 'thriller', 'suspense': 'thriller',
  'health & fitness': 'saude', 'technology': 'tecnologia', 'computers': 'tecnologia',
  'manga': 'manga', 'cooking': 'culinaria', 'sports & recreation': 'esportes',
};

function mapCategoria(categories) {
  if (!categories?.length) return '';
  const cat = (categories[0] || '').toLowerCase();
  if (CATEGORIA_MAP[cat]) return CATEGORIA_MAP[cat];
  for (const [key, val] of Object.entries(CATEGORIA_MAP)) {
    if (cat.includes(key) || key.includes(cat)) return val;
  }
  return '';
}

function melhorarCapa(url) {
  if (!url) return '';
  return url.replace('http://', 'https://').replace(/&edge=curl/, '').replace(/&zoom=\d/, '&zoom=1');
}

function toTitleCase(str) {
  if (!str) return str;
  if (str === str.toUpperCase()) {
    return str.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  }
  return str;
}

function capaPorId(id) {
  return `https://books.google.com/books/content?id=${id}&printsec=frontcover&img=1&zoom=1&source=gbs_api`;
}

function stripHtml(str) {
  if (!str) return '';
  return str
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ').replace(/&[a-z]+;/g, '').replace(/&#\d+;/g, '')
    .replace(/\s+/g, ' ').trim();
}

function parsearItens(items) {
  return (items || []).filter(item => item.volumeInfo?.title && item.volumeInfo?.authors?.length).map(item => ({
    id: item.id,
    titulo: toTitleCase(item.volumeInfo.title || ''),
    autor: toTitleCase((item.volumeInfo.authors || []).join(', ')),
    editora: item.volumeInfo.publisher || '',
    ano: item.volumeInfo.publishedDate ? item.volumeInfo.publishedDate.substring(0, 4) : '',
    capa: melhorarCapa(item.volumeInfo.imageLinks?.thumbnail || item.volumeInfo.imageLinks?.smallThumbnail || '') || capaPorId(item.id),
    descricao: stripHtml((item.volumeInfo.description || '').slice(0, 2000)),
    genero: mapCategoria(item.volumeInfo.categories || []),
  }));
}

function CadastrarLivro({ onVoltar, onSalvo }) {
  const { showToast } = useToast();
  const { isPago, openUpgrade } = usePlan();
  const [busca, setBusca] = useState('');
  const [sugestoes, setSugestoes] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [dropdownAberto, setDropdownAberto] = useState(false);

  const [titulo, setTitulo] = useState('');
  const [autor, setAutor] = useState('');
  const [editora, setEditora] = useState('');
  const [ano, setAno] = useState('');
  const [capaUrl, setCapaUrl] = useState('');
  const [capaLocal, setCapaLocal] = useState(null);
  const [capaPreview, setCapaPreview] = useState('');

  const [descricao, setDescricao] = useState('');
  const [genero, setGenero] = useState('');
  const [estrelas, setEstrelas] = useState(0);
  const [comentario, setComentario] = useState('');
  const [especial, setEspecial] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  const [scannerAberto, setScannerAberto] = useState(false);
  const [scannerErro, setScannerErro] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  const timerBusca = useRef(null);
  const fileInputRef = useRef(null);
  const scannerDivRef = useRef(null);
  const detectedRef = useRef(false);
  const lastCodeRef = useRef(null);
  const codeCountRef = useRef(0);

  const buscarNaGoogleBooks = useCallback(async (termo) => {
    if (termo.length < 3) {
      setSugestoes([]);
      setDropdownAberto(false);
      return;
    }
    // Se parece ISBN (10 ou 13 dígitos), usa o fluxo completo com fallbacks
    if (/^\d{10}$|^\d{13}$/.test(termo.replace(/[-\s]/g, ''))) {
      buscarPorIsbn(termo.replace(/[-\s]/g, ''), false); // false = não limpar o input
      return;
    }
    setBuscando(true);
    try {
      const url = `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(termo)}&maxResults=8&key=${GOOGLE_BOOKS_KEY}`;
      const res = await fetch(url);
      const data = await res.json();
      const items = parsearItens(data.items);
      setSugestoes(items);
      setDropdownAberto(items.length > 0);
    } catch {
      setSugestoes([]);
    } finally {
      setBuscando(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buscarPorIsbn = useCallback(async (code, limparBusca = true) => {
    if (limparBusca) setBusca('');
    setBuscando(true);
    setSugestoes([]);
    setDropdownAberto(false);
    try {
      let item = null;

      // Passo 1: BrasilAPI — melhor cobertura para livros brasileiros
      try {
        const res = await fetch(`https://brasilapi.com.br/api/isbn/v1/${code}`);
        if (res.ok) {
          const d = await res.json();
          if (d?.title) {
            item = {
              id: null,
              titulo: toTitleCase(d.title),
              autor: toTitleCase((d.authors || []).join(', ')),
              editora: d.publisher || '',
              ano: d.year ? String(d.year) : '',
              capa: melhorarCapa(d.cover_url || ''),
              descricao: stripHtml(d.synopsis || ''),
            };
          }
        }
      } catch {}

      // Passo 2: Open Library — fallback completo ou complemento de campos vazios
      const precisaComplemento = !item || !item.ano || !item.capa || !item.autor;
      if (precisaComplemento) {
        try {
          const res = await fetch(`https://openlibrary.org/api/books?bibkeys=ISBN:${code}&format=json&jscmd=data`);
          const data = await res.json();
          const olBook = data[`ISBN:${code}`];
          if (olBook?.title) {
            if (!item) {
              item = {
                id: null,
                titulo: toTitleCase(olBook.title),
                autor: toTitleCase((olBook.authors || []).map(a => a.name).join(', ')),
                editora: olBook.publishers?.[0]?.name || '',
                ano: '',
                capa: '',
                descricao: '',
              };
            }
            if (!item.autor) item.autor = toTitleCase((olBook.authors || []).map(a => a.name).join(', '));
            if (!item.ano) item.ano = (olBook.publish_date || '').replace(/.*(\d{4}).*/, '$1');
            if (!item.capa) item.capa = `https://covers.openlibrary.org/b/isbn/${code}-L.jpg`;
            // Tenta sinopse via Works API se ainda vazia
            if (!item.descricao) {
              const workKey = olBook.works?.[0]?.key;
              if (workKey) {
                try {
                  const workRes = await fetch(`https://openlibrary.org${workKey}.json`);
                  const workData = await workRes.json();
                  const desc = workData.description;
                  if (desc) item.descricao = stripHtml((typeof desc === 'string' ? desc : desc.value || '').slice(0, 2000));
                } catch {}
              }
            }
          }
        } catch {}
      }

      // Garante capa via Open Library como último recurso
      if (item && !item.capa) item.capa = `https://covers.openlibrary.org/b/isbn/${code}-L.jpg`;

      // Passo 3: Google Books — complementa autor, gênero e descrição ausentes
      if (item && (!item.autor || !item.genero || !item.descricao)) {
        try {
          const gbRes = await fetch(
            `https://www.googleapis.com/books/v1/volumes?q=isbn:${code}&maxResults=1&key=${GOOGLE_BOOKS_KEY}`
          );
          const gbData = await gbRes.json();
          const vi = gbData.items?.[0]?.volumeInfo;
          if (vi) {
            if (!item.autor && vi.authors?.length) item.autor = toTitleCase(vi.authors.join(', '));
            if (!item.genero && vi.categories?.length) item.genero = mapCategoria(vi.categories);
            if (!item.descricao && vi.description) item.descricao = stripHtml(vi.description.slice(0, 2000));
          }
        } catch {}
      }

      if (item) {
        setSugestoes([item]);
        setDropdownAberto(true);
      } else {
        showToast('Livro não encontrado. Tente buscar pelo título.', 'info');
      }
    } catch {
      showToast('Erro ao buscar livro. Tente pelo título.', 'error');
    } finally {
      setBuscando(false);
    }
  }, [showToast]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clearTimeout(timerBusca.current);
    timerBusca.current = setTimeout(() => buscarNaGoogleBooks(busca), 400);
    return () => clearTimeout(timerBusca.current);
  }, [busca, buscarNaGoogleBooks]);

  useEffect(() => {
    if (!scannerAberto) return;
    setScannerErro(false);
    detectedRef.current = false;
    lastCodeRef.current = null;
    codeCountRef.current = 0;

    const timer = setTimeout(() => {
      if (!scannerDivRef.current) return;
      Quagga.init({
        inputStream: {
          name: 'Live',
          type: 'LiveStream',
          target: scannerDivRef.current,
          constraints: {
            facingMode: 'environment',
            width: { min: 640, ideal: 1280 },
            height: { min: 480, ideal: 720 },
          },
        },
        decoder: {
          readers: ['ean_reader', 'ean_8_reader'],
          multiple: false,
        },
        locate: true,
      }, (err) => {
        if (err) { setScannerErro(true); return; }
        Quagga.start();
      });

      Quagga.onDetected((result) => {
        if (detectedRef.current) return;
        const code = result.codeResult?.code;
        if (!code || code.length < 8) return;

        if (code === lastCodeRef.current) {
          codeCountRef.current++;
          if (codeCountRef.current >= 2) {
            detectedRef.current = true;
            try { Quagga.stop(); } catch {}
            setScannerAberto(false);
            buscarPorIsbn(code);
          }
        } else {
          lastCodeRef.current = code;
          codeCountRef.current = 1;
        }
      });
    }, 150);

    return () => {
      clearTimeout(timer);
      try { Quagga.stop(); } catch {}
    };
  }, [scannerAberto, buscarPorIsbn]); // eslint-disable-line react-hooks/exhaustive-deps

  async function aplicarZoom(nivel) {
    const novoNivel = Math.min(3, Math.max(1, nivel));
    setZoomLevel(novoNivel);
    const video = scannerDivRef.current?.querySelector('video');
    if (!video) return;
    // Zoom nativo da câmera (Android / dispositivos com suporte)
    try {
      if (video.srcObject) {
        const track = video.srcObject.getVideoTracks()[0];
        const capabilities = track.getCapabilities?.();
        if (capabilities?.zoom) {
          const { min = 1, max = 4 } = capabilities.zoom;
          const z = Math.min(max, Math.max(min, min + (novoNivel - 1) * (max - min) / 2));
          await track.applyConstraints({ advanced: [{ zoom: z }] });
          return;
        }
      }
    } catch {}
    // Fallback CSS scale (iOS)
    const canvas = scannerDivRef.current?.querySelector('canvas');
    [video, canvas].forEach(el => {
      if (el) { el.style.transform = `scale(${novoNivel})`; el.style.transformOrigin = 'center center'; }
    });
  }

  function fecharScanner() {
    try { Quagga.stop(); } catch {}
    setScannerAberto(false);
    setZoomLevel(1);
  }

  async function selecionarLivro(livro) {
    setBusca('');
    setTitulo(livro.titulo);
    setAutor(livro.autor);
    setEditora(livro.editora);
    setAno(livro.ano);
    if (!capaLocal && livro.capa) setCapaUrl(livro.capa);
    setDescricao(livro.descricao || '');
    if (livro.genero) setGenero(livro.genero);
    setDropdownAberto(false);
    setSugestoes([]);

    if (livro.id) {
      // Item do Google Books: busca volume completo para metadados ricos
      try {
        const res = await fetch(
          `https://www.googleapis.com/books/v1/volumes/${livro.id}?fields=volumeInfo(title,authors,publisher,description,imageLinks,categories)&key=${GOOGLE_BOOKS_KEY}`
        );
        const data = await res.json();
        const vi = data.volumeInfo || {};
        if (vi.title) setTitulo(toTitleCase(vi.title));
        if (vi.authors?.length) setAutor(toTitleCase(vi.authors.join(', ')));
        if (vi.publisher) setEditora(vi.publisher);
        if (vi.description) setDescricao(stripHtml(vi.description.slice(0, 2000)));
        if (vi.categories?.length) setGenero(mapCategoria(vi.categories));
        if (!capaLocal) {
          const hqCapa = melhorarCapa(vi.imageLinks?.large || vi.imageLinks?.medium || vi.imageLinks?.thumbnail || vi.imageLinks?.smallThumbnail || '');
          if (hqCapa) setCapaUrl(hqCapa);
        }
      } catch {}
    }
  }

  function handleFotoChange(e) {
    const arquivo = e.target.files[0];
    if (!arquivo) return;
    setCapaLocal(arquivo);
    const reader = new FileReader();
    reader.onload = (ev) => setCapaPreview(ev.target.result);
    reader.readAsDataURL(arquivo);
  }

  function removerFoto() {
    setCapaLocal(null);
    setCapaPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleSalvar() {
    if (!titulo || !autor) {
      setErro('Título e autor são obrigatórios.');
      return;
    }
    setErro('');
    setCarregando(true);

    if (!isPago) {
      const { data: { user } } = await supabase.auth.getUser();
      const { count } = await supabase.from('livros').select('*', { count: 'exact', head: true }).eq('user_id', user.id);
      if (count >= LIMITES.livros) {
        setCarregando(false);
        openUpgrade('livros');
        return;
      }
    }

    let finalCapaUrl = capaUrl;

    if (capaLocal) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const ext = capaLocal.name.split('.').pop();
        const path = `${user.id}/${Date.now()}.${ext}`;
        const { data, error } = await supabase.storage.from('capas').upload(path, capaLocal, { upsert: true });
        if (!error) {
          const { data: urlData } = supabase.storage.from('capas').getPublicUrl(data.path);
          finalCapaUrl = urlData.publicUrl;
        } else {
          showToast('Não foi possível fazer upload da capa. Usando capa do Google Books.', 'info');
        }
      } catch {
        showToast('Não foi possível fazer upload da capa. Usando capa do Google Books.', 'info');
      }
    }

    const { data: { user } } = await supabase.auth.getUser();

    const { error } = await supabase.from('livros').insert({
      titulo,
      autor,
      editora,
      ano: ano ? parseInt(ano) : null,
      descricao: descricao || null,
      estrelas,
      comentario,
      genero: genero || null,
      disponivel: !especial,
      especial,
      capa_url: finalCapaUrl || null,
      user_id: user?.id || null,
    });

    setCarregando(false);
    if (error) setErro('Erro ao salvar. Tente novamente.');
    else { showToast('Livro adicionado com sucesso!', 'success'); onSalvo(); }
  }

  const imagemCapa = capaPreview || capaUrl;

  return (
    <>
    <div className="bg-paper font-sans" style={{height:'100dvh', overflowY:'auto'}}>
      {/* back-header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 px-[18px] pb-3"
        style={{paddingTop:'calc(env(safe-area-inset-top) + 44px)', background:'rgba(250,248,245,0.88)', backdropFilter:'blur(14px) saturate(160%)', WebkitBackdropFilter:'blur(14px) saturate(160%)', borderBottom:'1px solid #EBE8F0'}}>
        <button onClick={onVoltar}
          className="w-[38px] h-[38px] rounded-[12px] bg-white border border-[#EBE8F0] flex items-center justify-center flex-shrink-0 text-ink-700">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <h1 className="flex-1 text-[17px] font-extrabold tracking-tight text-ink-900 m-0">Novo livro</h1>
      </div>

      <div className="max-w-2xl mx-auto px-5 pb-10 pt-5">

        {/* capa preview + upload */}
        <div className="flex gap-4 mb-[22px]">
          <div className="w-[92px] h-[136px] rounded-[10px] overflow-hidden flex-shrink-0 flex items-center justify-center"
            style={{background:'#E8E4F0', boxShadow:'3px 3px 12px rgba(33,28,43,0.14)'}}>
            {imagemCapa
              ? <img src={imagemCapa} alt="Capa" className="w-full h-full object-cover" />
              : <BookOpen size={32} color="#C4BCDA" strokeWidth={1} />
            }
          </div>
          <div className="flex-1 flex flex-col justify-center gap-2">
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
              onChange={handleFotoChange} className="hidden" id="foto-capa" />
            <label htmlFor="foto-capa"
              className="flex flex-col items-center justify-center gap-1.5 rounded-[13px] cursor-pointer py-4 text-center"
              style={{border:'2px dashed #CFC5F7', background:'#F7F5FE', minHeight:90}}>
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5B3CE4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
              <span className="text-[12px] font-bold" style={{color:'#5B3CE4'}}>Adicionar capa</span>
              <span className="text-[11px] text-ink-400 font-medium">ou buscamos pra você</span>
            </label>
            {(capaPreview || capaUrl) && (
              <button onClick={() => { removerFoto(); setCapaUrl(''); }}
                className="text-[11.5px] text-ink-400 font-medium text-center border-0 bg-transparent cursor-pointer">
                Remover capa
              </button>
            )}
          </div>
        </div>

        {/* Google Books search */}
        <div className="mb-[17px] relative">
          <label className="block text-[12.5px] font-bold text-ink-500 mb-2">Buscar na Google Books</label>
          <div className="relative flex gap-2">
            <input type="text" placeholder="Digite o título ou ISBN..."
              value={busca} onChange={e => setBusca(e.target.value)}
              onFocus={() => sugestoes.length > 0 && setDropdownAberto(true)}
              onBlur={() => setTimeout(() => setDropdownAberto(false), 150)}
              className="flex-1 rounded-[13px] px-[14px] py-[13px] text-[14.5px] font-medium text-ink-900 bg-white outline-none"
              style={{border:'1px solid #DDD9E8', fontFamily:'inherit'}}
            />
            <button onClick={() => setScannerAberto(true)} type="button"
              className="w-[50px] rounded-[13px] flex items-center justify-center flex-shrink-0 border-0 cursor-pointer"
              style={{background:'#5B3CE4'}}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 7V5a2 2 0 0 1 2-2h2"/><path d="M16 3h2a2 2 0 0 1 2 2v2"/>
                <path d="M22 17v2a2 2 0 0 1-2 2h-2"/><path d="M8 21H6a2 2 0 0 1-2-2v-2"/>
                <rect x="7" y="7" width="10" height="10" rx="1"/>
              </svg>
            </button>
            {buscando && (
              <span className="absolute right-[62px] top-1/2 -translate-y-1/2 text-[12px] text-ink-300 animate-pulse">···</span>
            )}
          </div>
          {dropdownAberto && sugestoes.length > 0 && (
            <div className="absolute z-20 w-full top-full mt-1 bg-white rounded-[13px] overflow-hidden"
              style={{border:'1px solid #EBE8F0', boxShadow:'0 8px 24px rgba(33,28,43,0.12)'}}>
              {sugestoes.map(livro => (
                <button key={livro.id} onMouseDown={() => selecionarLivro(livro)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left border-b border-[#F3EFE9] last:border-0"
                  style={{background:'transparent'}}
                  onMouseEnter={e => e.currentTarget.style.background='#F7F5FE'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  {livro.capa
                    ? <img src={livro.capa} alt="" className="w-8 h-11 object-cover rounded-[6px] flex-shrink-0" />
                    : <div className="w-8 h-11 rounded-[6px] flex items-center justify-center flex-shrink-0" style={{background:'#EBE8F0'}}><BookOpen size={16} color="#C4BCDA" strokeWidth={1} /></div>
                  }
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-ink-900 truncate m-0">{livro.titulo}</p>
                    <p className="text-[11.5px] text-ink-400 font-medium truncate m-0">{livro.autor}</p>
                    {livro.ano && <p className="text-[11px] text-ink-300 m-0">{livro.ano}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Título */}
        <div className="mb-[17px]">
          <label className="block text-[12.5px] font-bold text-ink-500 mb-2">Título</label>
          <input type="text" placeholder="Ex.: Torto Arado" value={titulo}
            onChange={e => setTitulo(e.target.value)}
            className="w-full rounded-[13px] px-[14px] py-[13px] text-[14.5px] font-medium text-ink-900 bg-white outline-none"
            style={{border:'1px solid #DDD9E8', fontFamily:'inherit'}}
          />
        </div>

        {/* Autor */}
        <div className="mb-[17px]">
          <label className="block text-[12.5px] font-bold text-ink-500 mb-2">Autor</label>
          <input type="text" placeholder="Ex.: Itamar Vieira Junior" value={autor}
            onChange={e => setAutor(e.target.value)}
            className="w-full rounded-[13px] px-[14px] py-[13px] text-[14.5px] font-medium text-ink-900 bg-white outline-none"
            style={{border:'1px solid #DDD9E8', fontFamily:'inherit'}}
          />
        </div>

        {/* Editora + Ano */}
        <div className="flex gap-3 mb-[17px]">
          <div className="flex-1">
            <label className="block text-[12.5px] font-bold text-ink-500 mb-2">Editora</label>
            <input type="text" placeholder="Ex: Companhia das Letras" value={editora}
              onChange={e => setEditora(e.target.value)}
              className="w-full rounded-[13px] px-[14px] py-[13px] text-[14.5px] font-medium text-ink-900 bg-white outline-none"
              style={{border:'1px solid #DDD9E8', fontFamily:'inherit'}}
            />
          </div>
          <div className="w-24">
            <label className="block text-[12.5px] font-bold text-ink-500 mb-2">Ano</label>
            <input type="number" placeholder="2024" value={ano}
              onChange={e => setAno(e.target.value)}
              className="w-full rounded-[13px] px-[14px] py-[13px] text-[14.5px] font-medium text-ink-900 bg-white outline-none"
              style={{border:'1px solid #DDD9E8', fontFamily:'inherit'}}
            />
          </div>
        </div>

        {/* Gênero */}
        <div className="mb-[17px]">
          <label className="block text-[12.5px] font-bold text-ink-500 mb-2">
            Gênero <span className="font-medium text-ink-300">(opcional)</span>
          </label>
          <select value={genero} onChange={e => setGenero(e.target.value)}
            className="w-full rounded-[13px] px-[14px] py-[13px] text-[14.5px] font-medium text-ink-900 bg-white outline-none"
            style={{border:'1px solid #DDD9E8', fontFamily:'inherit', appearance:'none', WebkitAppearance:'none'}}>
            <option value="">Selecionar gênero</option>
            {GENEROS_LIST.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
          </select>
        </div>

        {/* Resumo */}
        <div className="mb-[17px]">
          <label className="block text-[12.5px] font-bold text-ink-500 mb-2">
            Resumo <span className="font-medium text-ink-300">(opcional)</span>
          </label>
          <textarea placeholder="Sinopse ou descrição do livro..." value={descricao}
            onChange={e => setDescricao(e.target.value)} rows={4}
            className="w-full rounded-[13px] px-[14px] py-[13px] text-[14.5px] font-medium text-ink-900 bg-white outline-none resize-none"
            style={{border:'1px solid #DDD9E8', fontFamily:'inherit', lineHeight:1.4}}
          />
        </div>

        {/* Avaliação */}
        <div className="mb-[17px]">
          <label className="block text-[12.5px] font-bold text-ink-500 mb-2">Avaliação</label>
          <div className="flex gap-1">
            {[1,2,3,4,5].map(s => (
              <button key={s} onClick={() => setEstrelas(s)}
                className="text-[28px] border-0 bg-transparent cursor-pointer leading-none p-0"
                style={{color: s <= estrelas ? '#FFAA00' : 'rgba(0,0,0,0.13)'}}>★</button>
            ))}
          </div>
        </div>

        {/* Comentário */}
        <div className="mb-[22px]">
          <label className="block text-[12.5px] font-bold text-ink-500 mb-2">
            Comentário <span className="font-medium text-ink-300">(opcional)</span>
          </label>
          <textarea placeholder="O que você achou desse livro?" value={comentario}
            onChange={e => setComentario(e.target.value)} rows={3}
            className="w-full rounded-[13px] px-[14px] py-[13px] text-[14.5px] font-medium text-ink-900 bg-white outline-none resize-none"
            style={{border:'1px solid #DDD9E8', fontFamily:'inherit', lineHeight:1.4}}
          />
        </div>

        {/* Livro especial */}
        <button type="button" onClick={() => setEspecial(v => !v)}
          className="w-full flex items-center justify-between px-4 py-[14px] rounded-[14px] mb-5 border-0 cursor-pointer text-left"
          style={{background: especial ? '#FFF8E1' : '#F5F4F7'}}>
          <div>
            <p className="text-[14px] font-bold m-0" style={{color: especial ? '#B87A00' : '#6B6478'}}>
              Livro especial
            </p>
            <p className="text-[12px] m-0 mt-0.5" style={{color: especial ? '#D4920A' : '#9A93A4'}}>
              {especial ? 'Privado — só aparece na sua estante, nunca publicamente' : 'Livro normal, visível e disponível para empréstimo'}
            </p>
          </div>
          <div className="flex-shrink-0 w-11 h-6 rounded-full transition-colors relative"
            style={{background: especial ? '#E6A800' : '#D1CDD8'}}>
            <div className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform"
              style={{transform: especial ? 'translateX(22px)' : 'translateX(2px)'}} />
          </div>
        </button>

        {erro && (
          <p className="text-[13px] font-medium rounded-[11px] px-4 py-3 mb-4"
            style={{background:'#FFF0ED', border:'1px solid #FFCFBF', color:'#D94F2E'}}>{erro}</p>
        )}

        <button onClick={handleSalvar} disabled={carregando || !titulo.trim() || !autor.trim()}
          className="w-full py-[15px] rounded-[14px] text-[15px] font-extrabold border-0 cursor-pointer transition-all"
          style={{background:'#5B3CE4', color:'#fff', boxShadow:'0 8px 20px rgba(91,60,228,0.30)', opacity:(titulo.trim() && autor.trim()) ? 1 : 0.5}}>
          {carregando ? 'Salvando...' : 'Salvar na estante'}
        </button>

      </div>
    </div>

    {/* Modal scanner de código de barras */}
    {scannerAberto && (
      <div style={{position:'fixed', top:0, left:0, right:0, height:'100dvh', zIndex:50, background:'#000', display:'flex', flexDirection:'column'}}>
        {/* Viewfinder */}
        <div className="relative flex-1 overflow-hidden">
          <div ref={scannerDivRef} className="w-full h-full" style={{background:'#000'}} />

          {/* Overlay escuro nas bordas com janela central */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="absolute inset-0" style={{background:'rgba(0,0,0,0.55)'}} />
            <div className="relative w-64 h-40 rounded-[16px]" style={{boxShadow:'0 0 0 9999px rgba(0,0,0,0.55)'}}>
              {/* Cantos do viewfinder */}
              <span className="absolute top-0 left-0 w-6 h-6 border-t-[3px] border-l-[3px] border-white rounded-tl-[10px]" />
              <span className="absolute top-0 right-0 w-6 h-6 border-t-[3px] border-r-[3px] border-white rounded-tr-[10px]" />
              <span className="absolute bottom-0 left-0 w-6 h-6 border-b-[3px] border-l-[3px] border-white rounded-bl-[10px]" />
              <span className="absolute bottom-0 right-0 w-6 h-6 border-b-[3px] border-r-[3px] border-white rounded-br-[10px]" />
            </div>
          </div>

          {scannerErro && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white rounded-[16px] px-6 py-5 mx-6 text-center">
                <p className="text-[15px] font-bold text-ink-900 mb-1">Câmera indisponível</p>
                <p className="text-[13px] text-ink-400">Verifique as permissões do navegador e tente novamente.</p>
              </div>
            </div>
          )}
        </div>

        {/* Rodapé */}
        <div className="px-6 py-5 flex flex-col items-center gap-4" style={{background:'#000'}}>
          <p className="text-[13px] font-medium text-center" style={{color:'rgba(255,255,255,0.6)'}}>
            Aponte a câmera para o código de barras do livro
          </p>
          {/* Controles de zoom */}
          <div className="flex items-center gap-4">
            <button onClick={() => aplicarZoom(zoomLevel - 0.5)} disabled={zoomLevel <= 1}
              className="w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold transition-opacity disabled:opacity-30"
              style={{background:'rgba(255,255,255,0.15)', color:'#fff'}}>−</button>
            <span className="text-[13px] font-semibold w-8 text-center" style={{color:'rgba(255,255,255,0.8)'}}>
              {zoomLevel % 1 === 0 ? `${zoomLevel}×` : `${zoomLevel}×`}
            </span>
            <button onClick={() => aplicarZoom(zoomLevel + 0.5)} disabled={zoomLevel >= 3}
              className="w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold transition-opacity disabled:opacity-30"
              style={{background:'rgba(255,255,255,0.15)', color:'#fff'}}>+</button>
          </div>
          <button onClick={fecharScanner}
            className="px-8 py-3 rounded-[14px] text-[14px] font-bold border-0 cursor-pointer"
            style={{background:'rgba(255,255,255,0.12)', color:'#fff'}}>
            Cancelar
          </button>
        </div>
      </div>
    )}
    </>
  );
}

export default CadastrarLivro;
